import { FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { useFormikContext } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CorporationsEntity } from "entities/Corporations/CorporationsEntity";
import { USA_COUNTRY_CODE } from "constants/constants";
import { useStyles } from "./GooglePlacesAddressFields.styles";
import { IGooglePlacesAddressFields } from "./GooglePlacesAddressFieldsInterfaces";

const GooglePlacesAddressFields = ({
    address1FieldName = "address1",
    address2FieldName = "address2",
    cityNameFieldName = "city_name",
    stateNameFieldName = "state_name",
    zipCodeFieldName = "zipCode",
    countryIdFieldName = "country_id",
    initialValues,
}: IGooglePlacesAddressFields) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { values, setFieldValue, touched, errors, handleChange } = useFormikContext<Object>();
    const { countries } = CorporationsEntity.GetCountries();

    const getCountryCodeFromCountryId = useCallback(
        (countryId: number) => {
            return countries.find((country) => country.id === countryId)?.code || null;
        },
        [countries],
    );

    const address1FieldNameKey = address1FieldName as keyof typeof values;
    const address2FieldNameKey = address2FieldName as keyof typeof values;
    const cityNameFieldNameKey = cityNameFieldName as keyof typeof values;
    const stateNameFieldNameKey = stateNameFieldName as keyof typeof values;
    const zipCodeFieldNameKey = zipCodeFieldName as keyof typeof values;
    const countryIdFieldNameKey = countryIdFieldName as keyof typeof values;

    const [countryCode, setCountryCode] = useState<string | null>(
        initialValues && initialValues?.[countryIdFieldNameKey]
            ? getCountryCodeFromCountryId(Number(initialValues[countryIdFieldNameKey]))
            : USA_COUNTRY_CODE,
    );
    const [stateName, setStateName] = useState<string | null>(null);
    const [cityName, setCityName] = useState<string | null>(null);
    const [zipCode, setZipCode] = useState<string | null>(null);

    const autoCompleteRef = useRef<google.maps.places.Autocomplete>();
    const inputRef = useRef<HTMLInputElement | null>(null);

    const options = useMemo(() => {
        return {
            ...(countryCode && { componentRestrictions: { country: countryCode } }),
            fields: ["address_components", "geometry", "icon", "name"],
            types: ["geocode"],
        };
    }, [countryCode]);

    const getCountryFromGooglePlaceCountryCode = useCallback(
        (countryCode: string) => {
            return countries.find((country) => country.code === countryCode);
        },
        [countries],
    );

    useEffect(() => {
        if (countryCode) {
            setFieldValue(countryIdFieldName, getCountryFromGooglePlaceCountryCode(countryCode)?.id ?? undefined);
        } else {
            setFieldValue(countryIdFieldName, initialValues?.[countryIdFieldNameKey] || "");
        }
    }, [
        countryCode,
        countryIdFieldName,
        countryIdFieldNameKey,
        getCountryFromGooglePlaceCountryCode,
        initialValues,
        setFieldValue,
    ]);

    useEffect(() => {
        if (stateName) {
            setFieldValue(stateNameFieldName, stateName);
        } else {
            setFieldValue(stateNameFieldName, initialValues?.[stateNameFieldNameKey] || "");
        }
    }, [stateName, setFieldValue, stateNameFieldName, initialValues, stateNameFieldNameKey]);

    useEffect(() => {
        if (cityName) {
            setFieldValue(cityNameFieldName, cityName);
        } else {
            setFieldValue(cityNameFieldName, initialValues?.[cityNameFieldNameKey] || "");
        }
    }, [cityName, cityNameFieldName, cityNameFieldNameKey, initialValues, setFieldValue]);

    useEffect(() => {
        if (zipCode) {
            setFieldValue(zipCodeFieldName, zipCode);
        } else {
            setFieldValue(zipCodeFieldName, initialValues?.[zipCodeFieldNameKey] || "");
        }
    }, [zipCode, setFieldValue, zipCodeFieldName, initialValues, zipCodeFieldNameKey]);

    useEffect(() => {
        if (inputRef.current) {
            autoCompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, options);

            autoCompleteRef.current.addListener("place_changed", async function () {
                const place = await autoCompleteRef?.current?.getPlace();
                setFieldValue(address1FieldName, place?.name);
                if (place && place.address_components) {
                    for (var i = 0; i < place.address_components.length; i += 1) {
                        var addressObj = place.address_components[i];

                        for (var j = 0; j < addressObj.types.length; j += 1) {
                            if (addressObj.types[j] === "locality") {
                                setCityName(addressObj.long_name);
                            }
                            if (addressObj.types[j] === "administrative_area_level_1") {
                                setStateName(addressObj.long_name);
                            }
                            if (addressObj.types[j] === "country") {
                                setCountryCode(addressObj.short_name);
                            }
                            if (addressObj.types[j] === "postal_code") {
                                setZipCode(addressObj.long_name);
                            } else {
                                setZipCode(null);
                            }
                        }
                    }
                }
            });
            return () => {
                window.google.maps.event.clearInstanceListeners(autoCompleteRef);
                window.google.maps.event.clearListeners(autoCompleteRef, "place_changed");
                autoCompleteRef.current?.set("place_changed", null);
            };
        }
    }, [address1FieldName, options, setFieldValue]);

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <FormControl className={classes.textField} fullWidth>
                        <InputLabel id="demo-simple-select-label">
                            {t("setup_corporation.corporation_form.country.label")}
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            name={countryIdFieldName}
                            className={classes.select}
                            value={values[countryIdFieldNameKey] || ""}
                            label={t("setup_corporation.corporation_form.country.label")}
                            error={touched[countryIdFieldNameKey] && Boolean(errors[countryIdFieldNameKey])}
                            onChange={(event) => {
                                setCountryCode(
                                    countries?.find((country) => country.id === +event.target.value)?.code || "",
                                );
                                setFieldValue(countryIdFieldName, +event.target.value);

                                setFieldValue(address1FieldName, "");
                                setFieldValue(address2FieldName, "");
                                setFieldValue(stateNameFieldName, "");
                                setFieldValue(cityNameFieldName, "");
                                setFieldValue(zipCodeFieldName, "");
                                setStateName(null);
                                setCityName(null);
                                setZipCode(null);

                                window.google.maps.event.clearInstanceListeners(autoCompleteRef);
                                window.google.maps.event.clearListeners(autoCompleteRef, "place_changed");
                                autoCompleteRef.current?.set("place_changed", null);
                            }}
                        >
                            {countries &&
                                countries.map((country, index) => (
                                    <MenuItem key={index} value={country.id}>
                                        {country.name}
                                    </MenuItem>
                                ))}
                        </Select>
                        {touched[countryIdFieldNameKey] && errors[countryIdFieldNameKey] && (
                            <FormHelperText className={classes.error}>
                                {t(errors[countryIdFieldNameKey] as string)}
                            </FormHelperText>
                        )}
                    </FormControl>
                </Grid>
            </Grid>

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        id="outlined-address-input"
                        inputRef={inputRef}
                        name={address1FieldName}
                        placeholder={
                            values[address1FieldNameKey]
                                ? t("setup_corporation.corporation_form.address1")
                                : t("setup_corporation.corporation_form.write_an_address")
                        }
                        type="text"
                        variant="outlined"
                        value={values[address1FieldNameKey]}
                        className={classes.textField}
                        label={
                            values[address1FieldNameKey]
                                ? t("setup_corporation.corporation_form.address1")
                                : t("setup_corporation.corporation_form.write_an_address")
                        }
                        onChange={handleChange}
                        error={touched[address1FieldNameKey] && Boolean(errors[address1FieldNameKey])}
                        helperText={
                            touched[address1FieldNameKey] &&
                            errors[address1FieldNameKey] &&
                            t(errors[address1FieldNameKey] as string)
                        }
                    />
                </Grid>
            </Grid>
            <TextField
                fullWidth
                id="outlined-address2-input"
                name={address2FieldName}
                type="text"
                variant="outlined"
                className={classes.textField}
                label={t("setup_corporation.corporation_form.address2")}
                value={values[address2FieldNameKey]}
                onChange={handleChange}
            />
            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <TextField
                        fullWidth
                        id="outlined-state_name-input"
                        name={stateNameFieldName}
                        type="text"
                        variant="outlined"
                        disabled
                        className={classes.textField}
                        label={t("setup_corporation.corporation_form.state.label")}
                        value={values[stateNameFieldNameKey]}
                        onChange={handleChange}
                        error={touched[stateNameFieldNameKey] && Boolean(errors[stateNameFieldNameKey])}
                        helperText={
                            touched[stateNameFieldNameKey] &&
                            errors[stateNameFieldNameKey] &&
                            t(errors[stateNameFieldNameKey] as string)
                        }
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        fullWidth
                        id="outlined-city_name-input"
                        name={cityNameFieldName}
                        type="text"
                        variant="outlined"
                        disabled
                        className={classes.textField}
                        label={t("setup_corporation.corporation_form.city.label")}
                        value={values[cityNameFieldNameKey]}
                        onChange={handleChange}
                        error={touched[cityNameFieldNameKey] && Boolean(errors[cityNameFieldNameKey])}
                        helperText={
                            touched[cityNameFieldNameKey] &&
                            errors[cityNameFieldNameKey] &&
                            t(errors[cityNameFieldNameKey] as string)
                        }
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        fullWidth
                        id="outlined-zipCode-input"
                        name={zipCodeFieldName}
                        type="text"
                        variant="outlined"
                        className={classes.textField}
                        label={t("setup_corporation.corporation_form.zip_code")}
                        value={values[zipCodeFieldNameKey]}
                        onChange={handleChange}
                        error={touched[zipCodeFieldNameKey] && Boolean(errors[zipCodeFieldNameKey])}
                        helperText={
                            touched[zipCodeFieldNameKey] &&
                            errors[zipCodeFieldNameKey] &&
                            t(errors[zipCodeFieldNameKey] as string)
                        }
                    />
                </Grid>
            </Grid>
        </>
    );
};

export default GooglePlacesAddressFields;
